import React from 'react';
import { Form, Button, Badge, Alert, Row, Col } from 'react-bootstrap';
import { FaCheckCircle, FaClock, FaLayerGroup } from 'react-icons/fa';
import {
  TRIAL_PRESET_DAYS,
  addDaysFromToday,
  formatSguTrialDateForDisplay,
  getSguLinkedCompanyNames,
  getSguTrialStatusMeta,
  isSguTrialMode,
} from './sguAccessUtils';
import './SguAccessSection.css';

const SguAccessSection = ({
  user,
  setUser,
  theme,
  canEdit,
  companyById,
  isSingleManagerType,
}) => {
  const trialActive = isSguTrialMode(user?.sguTrial);
  const linkedNames = getSguLinkedCompanyNames(user, companyById, isSingleManagerType);
  const trialDate = user?.sguTrial || '';
  const trialStatus = trialActive && trialDate ? getSguTrialStatusMeta(trialDate) : null;
  const missingTrialDate = trialActive && !trialDate;
  const todayMin = new Date().toISOString().slice(0, 10);
  const isDark = theme === 'dark';

  const handleAccessModeChange = (mode) => {
    setUser({
      ...user,
      sguTrial: mode === 'trial' ? (user?.sguTrial || '') : null,
    });
  };

  const handleTrialDateChange = (event) => {
    setUser({ ...user, sguTrial: event.target.value });
  };

  const handlePresetDays = (days) => {
    setUser({ ...user, sguTrial: addDaysFromToday(days) });
  };

  return (
    <div className={`sgu-access-section ${isDark ? 'sgu-access-section--dark' : ''}`}>
      <hr className={`my-3 ${isDark ? 'border-secondary' : ''}`} />

      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1 d-flex align-items-center gap-2">
            <FaLayerGroup className="text-purple" aria-hidden />
            Acesso ao SGU
          </h6>
          <small className={isDark ? 'text-light opacity-75' : 'text-muted'}>
            Escolha o tipo de acesso após o vínculo com a empresa.
          </small>
        </div>
        {!trialActive ? (
          <Badge bg="success" className="sgu-access-section__badge">
            <FaCheckCircle className="me-1" aria-hidden />
            Assinante
          </Badge>
        ) : trialStatus ? (
          <Badge bg={trialStatus.variant} className="sgu-access-section__badge">
            <FaClock className="me-1" aria-hidden />
            {trialStatus.label}
          </Badge>
        ) : (
          <Badge bg="secondary" className="sgu-access-section__badge">
            <FaClock className="me-1" aria-hidden />
            Trial — defina a data
          </Badge>
        )}
      </div>

      {linkedNames.length > 0 && (
        <div className={`sgu-access-section__linked mb-3 ${isDark ? 'sgu-access-section__linked--dark' : ''}`}>
          <span className="sgu-access-section__linked-label">Vinculado a</span>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {linkedNames.map((name) => (
              <Badge key={name} bg={isDark ? 'secondary' : 'light'} text={isDark ? 'light' : 'dark'} className="border">
                {name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div
        className="sgu-access-section__toggle btn-group w-100 mb-3"
        role="group"
        aria-label="Tipo de acesso SGU"
      >
        <Button
          type="button"
          variant={!trialActive ? 'success' : 'outline-secondary'}
          className={`flex-fill ${isDark && trialActive ? 'sgu-access-section__toggle-inactive--dark' : ''}`}
          disabled={!canEdit}
          onClick={() => handleAccessModeChange('subscriber')}
          aria-pressed={!trialActive}
        >
          <FaCheckCircle className="me-2" aria-hidden />
          Assinante
          <small className="d-block sgu-access-section__toggle-hint">Acesso sem prazo</small>
        </Button>
        <Button
          type="button"
          variant={trialActive ? 'warning' : 'outline-secondary'}
          className={`flex-fill ${isDark && !trialActive ? 'sgu-access-section__toggle-inactive--dark' : ''}`}
          disabled={!canEdit}
          onClick={() => handleAccessModeChange('trial')}
          aria-pressed={trialActive}
        >
          <FaClock className="me-2" aria-hidden />
          Trial
          <small className="d-block sgu-access-section__toggle-hint">Acesso temporário</small>
        </Button>
      </div>

      {!trialActive && (
        <p className={`mb-0 small ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>
          O usuário terá acesso completo ao SGU enquanto estiver vinculado à empresa.
        </p>
      )}

      {trialActive && (
        <div className={`sgu-access-section__trial-panel ${isDark ? 'sgu-access-section__trial-panel--dark' : ''}`}>
          <Form.Label className="fw-semibold mb-2">Validade do trial</Form.Label>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {TRIAL_PRESET_DAYS.map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant="outline-warning"
                disabled={!canEdit}
                onClick={() => handlePresetDays(days)}
              >
                +{days} dias
              </Button>
            ))}
          </div>

          <Row className="g-2 align-items-end">
            <Col xs={12} sm={6} md={5}>
              <Form.Label className={isDark ? 'text-light' : 'text-muted'}>
                Acesso válido até
              </Form.Label>
              <Form.Control
                type="date"
                value={trialDate}
                min={todayMin}
                onChange={handleTrialDateChange}
                disabled={!canEdit}
                isInvalid={missingTrialDate}
                className={isDark ? 'bg-dark text-light border-secondary' : ''}
              />
              {missingTrialDate && (
                <Form.Control.Feedback type="invalid">
                  Selecione uma data ou use um atalho acima.
                </Form.Control.Feedback>
              )}
            </Col>
            {trialDate && (
              <Col xs={12} sm={6} md={7}>
                <div className={`sgu-access-section__summary ${isDark ? 'sgu-access-section__summary--dark' : ''}`}>
                  <span className="sgu-access-section__summary-label">Resumo</span>
                  <p className="mb-0">
                    Acesso até <strong>{formatSguTrialDateForDisplay(trialDate)}</strong>
                    {trialStatus && (
                      <span className="ms-2">
                        · <Badge bg={trialStatus.variant}>{trialStatus.label}</Badge>
                      </span>
                    )}
                  </p>
                </div>
              </Col>
            )}
          </Row>

          {missingTrialDate && (
            <Alert variant="warning" className="mt-3 mb-0 py-2 small">
              Defina até quando o trial ficará ativo antes de salvar.
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default SguAccessSection;
